import { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ImageGenerator = () => {
  const [prompt, setPrompt] = useState('');
  const [style, setStyle] = useState('marketing');
  const [loading, setLoading] = useState(false);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [history, setHistory] = useState([]);
  const [error, setError] = useState('');

  const styles = [
    { key: 'marketing', name: '📈 Marketing', desc: 'Profissional, cores vibrantes, sucesso' },
    { key: 'product', name: '📦 Produto', desc: 'Showcase limpo, fundo branco' },
    { key: 'lifestyle', name: '🌟 Lifestyle', desc: 'Aspiracional, felicidade' },
    { key: 'social', name: '📱 Social Media', desc: 'Chamativo, cores fortes' },
    { key: 'motivational', name: '💪 Motivacional', desc: 'Inspirador, conquistas' }
  ];

  const promptSuggestions = [
    "Curso de Marketing Digital - Aprenda a ganhar dinheiro online",
    "Ebook de Finanças Pessoais - Liberdade financeira",
    "Produto de Emagrecimento - Transforme seu corpo",
    "Software de Gestão - Automatize seu negócio",
    "Mentoria de Sucesso - Alcance seus objetivos"
  ];

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/ai/generated-images`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(response.data);
    } catch (error) {
      console.error('Failed to fetch image history:', error);
    }
  };

  const generateImage = async () => {
    if (!prompt.trim()) {
      setError('Por favor, digite um prompt para gerar a imagem');
      return;
    }

    setLoading(true);
    setError('');
    setGeneratedImage(null);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/ai/generate-image`, {
        prompt: prompt,
        style: style,
        size: '1024x1024'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setGeneratedImage(response.data);
        fetchHistory();
      } else {
        setError('Erro ao gerar imagem');
      }
    } catch (error) {
      setError(error.response?.data?.detail || 'Erro ao gerar imagem');
    } finally {
      setLoading(false);
    }
  };

  const downloadImage = async (imageUrl, filename) => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'marketing-image.png';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (error) {
      console.error('Download error:', error);
      // Fallback: abrir em nova aba
      window.open(imageUrl, '_blank');
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">🎨 Gerador de Imagens IA</h1>
        <p className="text-gray-600">Crie imagens profissionais para suas campanhas de marketing usando DALL-E 3</p>
      </div>

      {/* Área de Geração */}
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Formulário */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Nova Imagem</h2>
            
            {/* Prompt */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Descreva a imagem que você quer criar
              </label>
              <textarea
                rows={3}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                placeholder="Ex: Imagem de marketing digital mostrando sucesso financeiro, laptop com gráficos..."
              />
            </div>

            {/* Sugestões de Prompt */}
            <div className="mb-4">
              <label className="block text-xs text-gray-500 mb-2">💡 Sugestões:</label>
              <div className="flex flex-wrap gap-2">
                {promptSuggestions.map((suggestion, i) => (
                  <button
                    key={i}
                    onClick={() => setPrompt(suggestion)}
                    className="text-xs bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded transition-colors"
                  >
                    {suggestion.substring(0, 30)}...
                  </button>
                ))}
              </div>
            </div>

            {/* Estilo */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Estilo da Imagem
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {styles.map((s) => (
                  <button
                    key={s.key}
                    onClick={() => setStyle(s.key)}
                    className={`p-2 rounded-lg border-2 text-left transition-colors ${
                      style === s.key
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="font-medium text-sm">{s.name}</div>
                    <div className="text-xs text-gray-500">{s.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Botão Gerar */}
            <button
              onClick={generateImage}
              disabled={loading || !prompt.trim()}
              className="w-full bg-gradient-to-r from-purple-600 to-blue-500 hover:from-purple-700 hover:to-blue-600 disabled:from-gray-400 disabled:to-gray-400 text-white font-bold py-3 px-6 rounded-lg"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Gerando imagem... (15-30 segundos)
                </span>
              ) : (
                '🎨 Gerar Imagem'
              )}
            </button>

            {error && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg">
                {error}
              </div>
            )}
          </div>

          {/* Preview */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Preview</h2>
            
            {generatedImage ? (
              <div className="space-y-4">
                <div className="border rounded-lg overflow-hidden">
                  <img
                    src={generatedImage.image_url}
                    alt="Generated"
                    className="w-full h-auto"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => downloadImage(generatedImage.image_url, `marketing-${Date.now()}.png`)}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-lg"
                  >
                    📥 Baixar Imagem
                  </button>
                  <button
                    onClick={() => navigator.clipboard.writeText(generatedImage.image_url)}
                    className="bg-gray-600 hover:bg-gray-700 text-white font-medium py-2 px-4 rounded-lg"
                  >
                    📋 Copiar URL
                  </button>
                </div>
                <div className="text-xs text-gray-500 bg-gray-50 p-2 rounded">
                  <strong>Prompt usado:</strong> {generatedImage.prompt_used?.substring(0, 200)}...
                </div>
              </div>
            ) : (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center text-gray-500">
                <div className="text-4xl mb-2">🖼️</div>
                <p>Sua imagem aparecerá aqui</p>
                <p className="text-sm">Digite um prompt e clique em "Gerar Imagem"</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Histórico */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b">
          <h2 className="text-xl font-semibold text-gray-900">📚 Histórico de Imagens</h2>
        </div>
        
        {history.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <p>Nenhuma imagem gerada ainda.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 p-4">
            {history.map((img) => (
              <div key={img.id} className="group relative">
                <img
                  src={img.image_url}
                  alt={img.original_prompt}
                  className="w-full h-32 object-cover rounded-lg"
                />
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all rounded-lg flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity space-x-2">
                    <button
                      onClick={() => downloadImage(img.image_url, `image-${img.id}.png`)}
                      className="bg-white text-gray-800 px-2 py-1 rounded text-sm font-medium"
                    >
                      📥
                    </button>
                    <button
                      onClick={() => window.open(img.image_url, '_blank')}
                      className="bg-white text-gray-800 px-2 py-1 rounded text-sm font-medium"
                    >
                      🔍
                    </button>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-1 truncate">{img.original_prompt}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-blue-800 mb-2">💡 Dicas para Melhores Resultados</h3>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• Seja específico: descreva cores, objetos, estilo e humor da imagem</li>
          <li>• Use palavras como "profissional", "moderno", "vibrante" para melhor qualidade</li>
          <li>• Para produtos, mencione o tipo e benefício principal</li>
          <li>• Evite texto muito longo na imagem (IA pode errar letras)</li>
          <li>• As imagens são geradas em 1024x1024 pixels, ideais para redes sociais</li>
        </ul>
      </div>
    </div>
  );
};

export default ImageGenerator;
